import type { UserRole } from '@lms/shared'
import {
  createContext,
  createElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

export interface AuthUser {
  id: string
  email: string
  name: string
  role: UserRole
  tenantId: string | null
  tenantSlug: string | null
}

interface AuthContextValue {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  backendUnavailable: boolean
  login: (token: string) => Promise<void>
  logout: () => void
  retryAuth: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const API_BASE = import.meta.env.VITE_API_URL || ''
const RETRY_INTERVAL_MS = 2000
const FETCH_TIMEOUT_MS = 5000

async function fetchCurrentUser(token: string, signal?: AbortSignal): Promise<AuthUser> {
  const res = await fetch(`${API_BASE}/api/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  })
  if (!res.ok) {
    const err = new Error('Failed to fetch user')
    ;(err as Error & { status: number }).status = res.status
    throw err
  }
  const body = await res.json()
  return body.data
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [backendUnavailable, setBackendUnavailable] = useState(false)
  const cancelledRef = useRef(false)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tryFetchRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    cancelledRef.current = false

    const token = localStorage.getItem('auth_token')
    if (!token) {
      setIsLoading(false)
      return
    }

    const tryFetch = async () => {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

      try {
        const fetchedUser = await fetchCurrentUser(token, controller.signal)
        clearTimeout(timeoutId)
        if (cancelledRef.current) return
        setUser(fetchedUser)
        setBackendUnavailable(false)
        setIsLoading(false)
      } catch (e) {
        clearTimeout(timeoutId)
        if (cancelledRef.current) return

        const status = (e as Error & { status?: number }).status

        if (status === 401 || status === 403) {
          // 認証失敗 — トークン削除してログイン画面へ
          localStorage.removeItem('auth_token')
          setIsLoading(false)
          return
        }

        // バックエンド未起動・ネットワークエラー — リトライ
        setBackendUnavailable(true)
        setIsLoading(false)
        retryTimerRef.current = setTimeout(tryFetch, RETRY_INTERVAL_MS)
      }
    }

    tryFetchRef.current = () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      tryFetch()
    }

    tryFetch()

    return () => {
      cancelledRef.current = true
      tryFetchRef.current = null
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
      }
    }
  }, [])

  const login = useCallback(async (token: string) => {
    localStorage.setItem('auth_token', token)
    const u = await fetchCurrentUser(token)
    setUser(u)
    setBackendUnavailable(false)
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token')
    setUser(null)
  }, [])

  const retryAuth = useCallback(() => {
    tryFetchRef.current?.()
  }, [])

  return createElement(
    AuthContext.Provider,
    {
      value: {
        user,
        isLoading,
        isAuthenticated: !!user,
        backendUnavailable,
        login,
        logout,
        retryAuth,
      },
    },
    children,
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
